import { create } from 'zustand';
import { DocumentCategory, Document } from '../types/documents.types';

interface DocumentsState {
  // Filters
  selectedCategory: DocumentCategory | null;
  selectedTags: string[];
  searchQuery: string;
  
  // Hierarchical navigation filters
  selectedDomainUuid: string | null;
  selectedFolderUuid: string | null;
  selectedEmployeeUuid: string | null;
  dateFrom: string | null;
  dateTo: string | null;

  // UI State
  isUploadModalOpen: boolean;
  isPreviewModalOpen: boolean;
  isCreateModalOpen: boolean;
  isDeleteModalOpen: boolean;
  previewDocumentUuid: string | null;
  deleteDocument: Document | null;

  // Pagination
  currentPage: number;
  pageSize: number;

  // Actions
  setSelectedCategory: (category: DocumentCategory | null) => void;
  setSelectedTags: (tags: string[]) => void;
  setSearchQuery: (query: string) => void;
  setSelectedDomainUuid: (uuid: string | null) => void;
  setSelectedFolderUuid: (uuid: string | null) => void;
  setSelectedEmployeeUuid: (uuid: string | null) => void;
  setDateFrom: (date: string | null) => void;
  setDateTo: (date: string | null) => void;
  openUploadModal: () => void;
  closeUploadModal: () => void;
  openCreateModal: () => void;
  closeCreateModal: () => void;
  openPreviewModal: (uuid: string) => void;
  closePreviewModal: () => void;
  openDeleteModal: (document: Document) => void;
  closeDeleteModal: () => void;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  resetFilters: () => void;
}

const initialState = {
  selectedCategory: null,
  selectedTags: [],
  searchQuery: '',
  selectedDomainUuid: null,
  selectedFolderUuid: null,
  selectedEmployeeUuid: null,
  dateFrom: null,
  dateTo: null,
  isUploadModalOpen: false,
  isCreateModalOpen: false,
  isPreviewModalOpen: false,
  isDeleteModalOpen: false,
  previewDocumentUuid: null,
  deleteDocument: null,
  currentPage: 1,
  pageSize: 20,
};

export const useDocumentsStore = create<DocumentsState>()(set => ({
  ...initialState,

  setSelectedCategory: category =>
    set({ selectedCategory: category, currentPage: 1 }),
  setSelectedTags: tags => set({ selectedTags: tags, currentPage: 1 }),
  setSearchQuery: query => set({ searchQuery: query, currentPage: 1 }),
  setSelectedDomainUuid: uuid => {
    set({ 
      selectedDomainUuid: uuid, 
      selectedFolderUuid: null, // Reset folder when domain changes
      selectedEmployeeUuid: null, // Reset employee when domain changes
      currentPage: 1 
    });
  },
  setSelectedFolderUuid: uuid => {
    set({ 
      selectedFolderUuid: uuid, 
      selectedEmployeeUuid: null, // Reset employee when folder changes
      currentPage: 1 
    });
  },
  setSelectedEmployeeUuid: uuid =>
    set({ selectedEmployeeUuid: uuid, currentPage: 1 }),
  setDateFrom: date => set({ dateFrom: date, currentPage: 1 }),
  setDateTo: date => set({ dateTo: date, currentPage: 1 }),
  openUploadModal: () => set({ isUploadModalOpen: true }),
  closeUploadModal: () => set({ isUploadModalOpen: false }),
  openCreateModal: () => set({ isCreateModalOpen: true }),
  closeCreateModal: () => set({ isCreateModalOpen: false }),
  openPreviewModal: uuid =>
    set({ isPreviewModalOpen: true, previewDocumentUuid: uuid }),
  closePreviewModal: () =>
    set({ isPreviewModalOpen: false, previewDocumentUuid: null }),
  openDeleteModal: document =>
    set({ isDeleteModalOpen: true, deleteDocument: document }),
  closeDeleteModal: () =>
    set({ isDeleteModalOpen: false, deleteDocument: null }),
  setCurrentPage: page => set({ currentPage: page }),
  setPageSize: size => set({ pageSize: size, currentPage: 1 }),
  resetFilters: () =>
    set({
      selectedCategory: null,
      selectedTags: [],
      searchQuery: '',
      selectedDomainUuid: null,
      selectedFolderUuid: null,
      selectedEmployeeUuid: null,
      dateFrom: null,
      dateTo: null,
      currentPage: 1,
    }),
}));

